import { apiEndpoints } from './common'

import { getAccessToken, makeRequestUrl } from '../app/helpers'
import { get, post } from '../request'

// ---- Cash Register (oh_cash_register) ----
// The ledger is a view over PayIn rows (mode cash/cheque) — collections are
// recorded by the existing Orders → Collect flow, never here. These calls only
// read the register and record bank deposits.

// One centre's full ledger + KPI strip, plus the reason and route catalogues
// the screen renders (never hardcoded here — the server owns those labels).
// {kpis, results, reasons, routes, is_finance, start_date, escalation_days}.
const fetchCashLedger = async (centreId) => {
  const url = makeRequestUrl(apiEndpoints.cashRegisterLedger, {
    centre: centreId
  })
  const response = await get(url)
  return response.data
}

// DRF speaks in `detail` and bare arrays; the axios interceptor in request.js
// only knows how to read `data.message`, so every validation message this
// endpoint raises ("please pick why", "already settled — ask Finance") would
// otherwise reach the user as the generic "Whoopsie-Daisy". Pull the server's
// own words out of whichever shape came back. Same extraction the deposit call
// below already does by hand.
const drfMessage = (error, fallback) => {
  const status = error?.status || error?.response?.status
  // A 404 here means the route itself is missing (this view never raises one),
  // so the useful thing to say is "not deployed", not DRF's bare "Not found."
  if (status === 404) return 'This is not available on the server yet.'
  const data = error?.response?.data
  if (Array.isArray(data) && data.length) return String(data[0])
  if (data && typeof data === 'object') {
    if (data.detail) return String(data.detail)
    if (data.message) return String(data.message)
    // A field-keyed 400: {"amount_received": ["..."]}
    const first = Object.values(data).find((v) => Array.isArray(v) && v.length)
    if (first) return String(first[0])
  }
  return error?.message || fallback
}

// Record what a collection actually brought in. The booked invoice value is
// what the register used to count; this is the cash that really arrived, plus
// the reason it differs, plus the notes it arrived as. Returns the refreshed
// ledger row.
//
// `counts` is {"500": 12, "200": 20, …}. Send it and the server checks it adds
// up to `amountReceived` — the two are one statement made twice and must not
// be allowed to drift. Omit it entirely to leave an existing count untouched.
const saveCashReceived = async ({
  payInId,
  amountReceived,
  reason,
  note,
  counts
}) => {
  try {
    const response = await post(apiEndpoints.cashRegisterReceived, {
      pay_in: payInId,
      amount_received: amountReceived,
      reason: reason || '',
      note: note || '',
      ...(counts ? { counts } : {})
    })
    return response.data
  } catch (error) {
    throw new Error(drfMessage(error, 'Could not save the received amount.'))
  }
}

// Record one settlement: the selected collections + where the cash went, in a
// single multipart POST (the API uploads the challan to S3
// server-side and computes the amount from what was received).
// `route` is 'bank' | 'handover' | 'pre_register'; the challan is required
// only for a bank run.
// Raw fetch (not the axios instance, which forces application/json) so the
// browser sets the multipart boundary; replicate the app's auth headers.
const createCashDeposit = async ({
  centreId,
  route,
  bank,
  handedTo,
  depositedOn,
  note,
  payInIds,
  file
}) => {
  const form = new FormData()
  form.append('centre', centreId)
  form.append('route', route || 'bank')
  if (bank) form.append('bank', bank)
  if (handedTo) form.append('handed_to', handedTo)
  form.append('deposited_on', depositedOn)
  if (note) form.append('note', note)
  form.append('pay_ins', JSON.stringify(payInIds))
  if (file) form.append('file', file)
  const token = getAccessToken()
  const response = await fetch(apiEndpoints.cashRegisterDeposits, {
    method: 'POST',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      source: 'alfred'
    },
    body: form
  })
  if (!response.ok) {
    let message = 'Could not record the deposit. Please try again.'
    try {
      const err = await response.json()
      message =
        (Array.isArray(err) ? err[0] : null) ||
        err.detail ||
        err.message ||
        message
    } catch (e) {
      /* non-JSON error body */
    }
    throw new Error(message)
  }
  return response.json()
}

// HQ: per-centre in-hand roll-up, worst first.
const fetchCashHqRollup = async () => {
  const response = await get(apiEndpoints.cashRegisterHqRollup)
  return response.data
}

// HQ: the challan ledger — one card per deposit. {count, results}.
const fetchCashHqDeposits = async (queries = {}) => {
  const url = makeRequestUrl(apiEndpoints.cashRegisterHqDeposits, queries)
  const response = await get(url)
  return response.data
}

// ---- HQ Finance's own drawer ----
// Before this existed, a handover marked the centre's row settled and the
// money left the register entirely — nothing could say what HQ was holding.

// Everything at the HQ desk in one call: envelopes in transit, envelopes in
// the safe, and the nine note counts that should physically be in the drawer.
const fetchCashHqSafe = async () => {
  const response = await get(apiEndpoints.cashRegisterHqSafe)
  return response.data
}

// HQ opens one envelope and counts it — the second count, and the only check
// anybody performs on this money. A difference from the centre's figure is
// allowed through, but has to carry a reason.
const receiveCashAtHq = async ({
  payInId,
  counts,
  receivedOn,
  receivedFrom,
  reason,
  note
}) => {
  try {
    const response = await post(apiEndpoints.cashRegisterHqReceive, {
      pay_in: payInId,
      counts,
      received_on: receivedOn,
      received_from: receivedFrom || '',
      reason: reason || '',
      note: note || ''
    })
    return response.data
  } catch (error) {
    throw new Error(drfMessage(error, 'Could not receive that envelope.'))
  }
}

// Cash leaving the HQ safe: a bank run over whole envelopes (challan
// required), or a handout against no envelope at all ("given to Satya for the
// GST payment"), where the note counts are the only record there is.
// Raw fetch for the same reason as createCashDeposit — multipart needs the
// browser to set its own boundary.
const createCashHqDeposit = async ({
  kind,
  bank,
  givenTo,
  purpose,
  movedOn,
  note,
  receiptIds,
  counts,
  file
}) => {
  const form = new FormData()
  form.append('kind', kind || 'bank')
  if (bank) form.append('bank', bank)
  if (givenTo) form.append('given_to', givenTo)
  if (purpose) form.append('purpose', purpose)
  form.append('moved_on', movedOn)
  if (note) form.append('note', note)
  form.append('receipts', JSON.stringify(receiptIds || []))
  if (counts) form.append('counts', JSON.stringify(counts))
  if (file) form.append('file', file)
  const token = getAccessToken()
  const response = await fetch(apiEndpoints.cashRegisterHqSafeDeposits, {
    method: 'POST',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      source: 'alfred'
    },
    body: form
  })
  if (!response.ok) {
    let message = 'Could not record that movement. Please try again.'
    try {
      const err = await response.json()
      message =
        (Array.isArray(err) ? err[0] : null) ||
        err.detail ||
        err.message ||
        message
    } catch (e) {
      /* non-JSON error body */
    }
    throw new Error(message)
  }
  return response.json()
}

// HQ: the movement history out of the safe. {count, results}.
const fetchCashHqSafeDeposits = async (queries = {}) => {
  const url = makeRequestUrl(apiEndpoints.cashRegisterHqSafeDeposits, queries)
  const response = await get(url)
  return response.data
}

export {
  createCashDeposit,
  createCashHqDeposit,
  fetchCashHqDeposits,
  fetchCashHqRollup,
  fetchCashHqSafe,
  fetchCashHqSafeDeposits,
  fetchCashLedger,
  receiveCashAtHq,
  saveCashReceived
}
